import { DataField, H2, Section, CheckList } from "./common-components";

/** Chronic disease keywords for "Multiple acute conditions and / or one or more chronic diseases". */
const CHRONIC_DISEASE_KEYWORDS = [
  "angina", "arthritis", "asthma", "cardiovascular", "hypertension", "stroke",
  "copd", "respiratory", "diabetes", "heart attack", "mental illness",
  "inflammatory bowel", "osteoporosis", "anxiety", "depression", "bipolar",
  "schizophrenia", "parkinson", "multiple sclerosis", "dialysis",
  "liver transplant", "kidney transplant", "lung transplant", "musculoskeletal",
  "cystic kidney", "hemodialysis",
];

const RENAL_KEYWORDS = ["kidney dialysis", "hemodialysis", "cystic kidney", "kidney transplant", "dialysis"];
const LIVER_KEYWORDS = ["liver disease", "liver transplant", "alcoholic liver disease", "cholestatic liver disease"];

function getMedicalConditions(patient) {
  const raw = patient?.medicalHistoryInfo?.medicalConditions;
  if (Array.isArray(raw)) return raw;
  if (raw?.medicalConditions && Array.isArray(raw.medicalConditions)) return raw.medicalConditions;
  return [];
}

function getActiveConditionNames(patient) {
  const conditions = getMedicalConditions(patient);
  const active = conditions.filter((c) => c.status === "active" || c.status == null);
  return active.map((c) => (c.conditionName || c.name || "").toLowerCase());
}

export const ClinicalNeedCheckList = ({
  formType,
  patient,
  preAssessment,
  medications = {},
  patientMedications = [],
  clinicalAssessment = {},
}) => {
  const conditionNames = getActiveConditionNames(patient || {});
  const reasonsForAMR = preAssessment?.reasonsForMedicationReview ?? [];
  const medReviews = medications?.medications || [];

  // 1) 3 or more chronic conditions = 3+ medical conditions in patient chart
  const threeOrMoreChronic = conditionNames.length >= 3;

  // 2) Multiple acute (2+ any medical conditions) OR one or more chronic disease from list
  const twoOrMoreConditions = conditionNames.length >= 2;
  const hasChronicDisease = conditionNames.some((name) =>
    CHRONIC_DISEASE_KEYWORDS.some((kw) => name.includes(kw))
  );
  const multipleAcuteOrChronic = twoOrMoreConditions || hasChronicDisease;

  // 3) Non-Rx in Additional Medications (medicationType "Non-Prescription" in review or on med)
  const hasNonPrescription = medReviews.some((m) => {
    const mt = (m.medicationType || "").toLowerCase();
    if (mt.includes("non-prescription") || mt === "non-prescription") return true;
    const med = patientMedications.find((pm) => pm.id === m.medicationId);
    return (med?.medicationType || "").toLowerCase().includes("non-prescription");
  });

  // 4) Natural health products in Additional Medications (medicationType "Natural-Product" / herbal)
  const hasNaturalHealth = medReviews.some((m) => {
    const mt = (m.medicationType || "").toLowerCase();
    if (mt.includes("natural") || mt.includes("herbal")) return true;
    const med = patientMedications.find((pm) => pm.id === m.medicationId);
    return (med?.medicationType || "").toLowerCase().includes("natural");
  });

  // 5) "Is the medication working?" answered "no" at any time during review
  const effectivenessNo = medReviews.some((m) => {
    const v = (m.effectiveness || m.working || "").toString().toLowerCase();
    return v === "no";
  });

  // 6) Any one intervention created
  const hasIntervention = medReviews.some((m) => {
    const i = m.intervention;
    return i && (i.medicationName || i.whoChangedMedication || i.override);
  });

  // 7) More than one health care provider (unique prescriber names from review meds)
  const reviewMedIds = new Set(medReviews.map((m) => m.medicationId));
  const prescriberNames = (patientMedications || [])
    .filter((pm) => reviewMedIds.has(pm.id))
    .map((pm) => (pm.prescriberName || "").trim())
    .filter(Boolean);
  const multiplePrescribers = new Set(prescriberNames).size > 1;

  // 8) Adherence high risk and late refills (refill === "Late" and risk present)
  const hasAdherenceRisk =
    (clinicalAssessment?.routine !== "No" && clinicalAssessment?.routineType === "Usually") ||
    !!clinicalAssessment?.compliance;
  const lateRefill = (clinicalAssessment?.refill || "").toLowerCase() === "late";
  const issuesEarlyLateRefills = hasAdherenceRisk && lateRefill;

  // 9) Non-adherence identified (icon / flag: use adherence risk or patient flag)
  const nonAdherence =
    !!patient?.isMedicationAdherence || (hasAdherenceRisk && (clinicalAssessment?.compliance || lateRefill));

  // 10) Adherence issue identified → patient confused about regimen (treat as any adherence risk for now)
  const adherenceIssueIdentified = hasAdherenceRisk;

  // 11) Medications that require routine laboratory monitoring
  const medRequiresLabFromReasons = reasonsForAMR.some((x) => (x || "").toLowerCase().includes("lab"));
  const medRequiresLabFromReviews = medReviews.some((m) => {
    return (
      m?.laboratoryRecommendations ||
      m?.laboratoryRecommendation ||
      m?.requiresLabMonitoring ||
      (m?.recommendations && m.recommendations.laboratory)
    );
  });
  const medicationListCompleteFlag = Boolean(medications?.medicationListComplete);
  const medicationsRequireLab = Boolean(medRequiresLabFromReasons || medRequiresLabFromReviews || medicationListCompleteFlag);

  // 12) Abnormal lab results (try to infer from reasons or review flags)
  const abnormalLabFromReasons = reasonsForAMR.some((x) => (x || "").toLowerCase().includes("abnormal lab") || (x || "").toLowerCase().includes("abnormal"));
  const abnormalLabFromReviews = medReviews.some((m) => m?.abnormalLabResults || (m?.labs && m.labs.some((l) => l?.abnormal)));
  const abnormalLabResults = Boolean(abnormalLabFromReasons || abnormalLabFromReviews);

  // 13–15) From reason(s) for AMR selected at start of service
  const plannedAdmission = reasonsForAMR.some((x) =>
    (x || "").includes("I have a planned upcoming hospital admission.")
  );
  const dischargeTransition = reasonsForAMR.some((x) =>
    (x || "").includes("I had a recent discharge from hospital.")
  );
  const initiateCompliance = reasonsForAMR.some((x) =>
    (x || "").toLowerCase().includes("initiate compliance packaging")
  );

  // 16) Renal: Characteristics includes DIALYSIS or medical conditions include kidney dialysis, hemodialysis, cystic kidney, kidney transplant
  const characteristicsList = (patient?.characteristics || "")
    .split(",")
    .map((s) => (s || "").trim().toLowerCase())
    .filter(Boolean);
  const characteristicsDialysis = characteristicsList.some((c) => c.includes("dialysis"));
  const hasRenalCondition = conditionNames.some((name) =>
    RENAL_KEYWORDS.some((kw) => name.includes(kw))
  );
  const poorRenal = characteristicsDialysis || hasRenalCondition;

  // 17) Liver: medical conditions include liver disease, liver transplant, alcoholic liver disease, cholestatic liver disease
  const poorLiver = conditionNames.some((name) =>
    LIVER_KEYWORDS.some((kw) => name.includes(kw))
  );

  const reasons = [
    { label: "3 or more chronic conditions", checked: threeOrMoreChronic },
    { label: "Multiple acute conditions and / or one or more chronic diseases", checked: multipleAcuteOrChronic },
    { label: "Medication regimen that includes one or more non prescription medications", checked: hasNonPrescription },
    { label: "Medication regimen that includes one or more natural health products", checked: hasNaturalHealth },
    { label: "Symptoms that seem unaddressed by current pharmacotherapy", checked: effectivenessNo },
    { label: "Potential drug therapy problem that may be prevented", checked: hasIntervention },
    { label: "Multiple Prescribers", checked: multiplePrescribers },
    { label: "Issues relating to early +/ or late refills", checked: issuesEarlyLateRefills },
    { label: "Non-adherence", checked: nonAdherence },
    { label: "Patient seems confused about medication regimen", checked: adherenceIssueIdentified },
    { label: "Medications that require routine laboratory monitoring", checked: medicationsRequireLab },
    { label: "Abnormal lab results", checked: abnormalLabResults },
    { label: "Planned admission to a hospital or other health institution", checked: plannedAdmission },
    { label: "Discharge / transition from hospital to community or other healthcare institution", checked: dischargeTransition },
    { label: "Initiate Compliance Packaging", checked: initiateCompliance },
    { label: "Known or suspected poor or unstable renal function", checked: poorRenal },
    { label: "Known or suspected poor or unstable liver function", checked: poorLiver },
    { label: "Other:", checked: false },
  ];

  return (
    <Section formType={formType}>
      <H2>Clinical Need For Service</H2>
      <div className="flex flex-col gap-1">
        <DataField value={"Patient Characteristics Contributing to the Need for the Medication Review"} />
        <CheckList list={reasons} />
      </div>
    </Section>
  );
};
